/**
 * Utility functions for manipulating page elements.
 */

import { useDateFormat } from "@vueuse/core";
import DOMPurify from 'dompurify';

/**
 * Focuses on the first invalid input element on the page.
 */
export const focusFirstInvalidInput = () => {
    const invalidInput = document.querySelector('.is-invalid') || document.querySelector('.p-invalid');
    if (invalidInput) {
        invalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalidInput.focus();
    }
};


/**
 * Focuses on the error message element on the page by scrolling it into view.
 * The function searches for an element with the ID `error-msg` and, if found,
 * ensures it is brought into the user's viewport.
 */
export const focusErrorMessage = () => {
    const errorElement = document.querySelector('#error-msg');
    if (errorElement) {
        scrollIntoView(errorElement);
    }
};

export const focusFirstEnabledInput = (containerId) => {
    if (!containerId) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    const firstEnabledInputs = container.querySelectorAll('input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled]), .p-select-label:not([disabled])');
    const firstEnabledInput = firstEnabledInputs[0];
    if (firstEnabledInput) {
        firstEnabledInput.focus();
        firstEnabledInput.select?.();
    }
};

/**
 * Scrolls the given element into view.
 * @param {Element} element - The DOM element to scroll into view.
 */
export const scrollIntoView = (element) => {
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth' });
};

/**
 * Scrolls the page to the top.
 */
export const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Sets focus on the element identified by the given selector.
 * @param {string} selector - The ID or inputId attribute of the element to focus.
 */
export const setFocus = (selector) => {
    if (!selector) return;
    const element = getElementById(selector);
    if (element) {
        element.focus();
        element?.select?.();
    } 
};

export const getElementById = (id) => {
    if (!id) return null;
    const element = document.getElementById(id);
    if (element) {
        return element;
    } else {
        const inputElement = document.querySelector(`[inputId="${id}"]`);
        return inputElement || null;
    }
};

/**
 * Returns the head title by appending the provided title with the application title.
 * @param {string} title - The title to be appended.
 * @returns {string} The head title.
 */
export const getHeadTitle = (title) => {
    return title + ' - ' + import.meta.env.VITE_APP_TITLE;
};

/**
 * Delays execution for the specified number of milliseconds.
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {Promise<void>} A promise that resolves after the delay.
 */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Formats a date into 'MM/DD/YYYY' format.
 * @param {Date|string} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const formattedDate = (date) => {
    if (!date) return '';
    const formatted = useDateFormat(date, 'MM/DD/YYYY');
    return formatted.value;
};

/**
 * Formats a number with locale-specific formatting.
 * @param {number} number - The number to format.
 * @param {string} [locale='en-US'] - The locale to use for formatting.
 * @returns {string|number} The formatted number or 0 if invalid.
 */
export const formatNumber = (number, locale = 'en-US') => {
    if (number === null || number === undefined || isNaN(number)) return 0;
    try {
        return number.toLocaleString(locale);
    } catch (e) {
        return number.toString();
    }
};

/**
 * Formats a nullable number with locale-specific formatting.
 * @param {number|null|undefined} number - The number to format.
 * @param {string} [locale='en-US'] - The locale to use for formatting.
 * @returns {string} The formatted number or an empty string if invalid.
 */
export const formatNullableNumber = (number, locale = 'en-US') => {
    if (number === null || number === undefined || isNaN(number)) return '';
    try {
        return number.toLocaleString(locale);
    } catch (e) {
        return number.toString();
    }
};

/**
 * Formats a number as currency.
 * @param {number} number - The number to format.
 * @param {Object} [options={}] - Formatting options.
 * @param {string} [options.locale='en-US'] - The locale to use.
 * @param {string} [options.currency='USD'] - The currency code.
 * @param {number} [options.minimumFractionDigits=0] - Minimum fraction digits.
 * @param {number} [options.maximumFractionDigits=0] - Maximum fraction digits.
 * @param {boolean} [options.returnNull=false] - Whether to return null for invalid input.
 * @returns {string|null} The formatted currency string or null.
 */
export const formatCurrency = (number, options = {}) => {
    const { locale = 'en-US', currency = 'USD', minimumFractionDigits = 0, maximumFractionDigits = 0, returnNull = false } = options;

    if (number === null || number === undefined || isNaN(number)) {
        return returnNull ? null : new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits, maximumFractionDigits }).format(0);
    }
    return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits, maximumFractionDigits }).format(number);
};

/**
 * Formats a number as a percentage.
 * @param {number} number - The number to format.
 * @param {Object} [options={}] - Formatting options.
 * @param {string} [options.locale='en-US'] - The locale to use.
 * @param {number} [options.minimumFractionDigits=0] - Minimum fraction digits.
 * @param {number} [options.maximumFractionDigits=0] - Maximum fraction digits.
 * @returns {string} The formatted percentage string.
 */
export const formatPercentage = (number, options = {}) => {
    const { locale = 'en-US', minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;

    if (number === null || number === undefined || isNaN(number)) {
        return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits, maximumFractionDigits }).format(0);
    }

    if (number < 0 || number > 1) {
        number = number / 100; // Convert to decimal if it's a percentage value
    }
    
    return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits, maximumFractionDigits }).format(number);
};

/**
 * Sanitizes an HTML string to remove potentially unsafe content.
 * @param {string} html - The HTML string to sanitize.
 * @returns {string} The sanitized HTML string.
 */
export const sanitizeHTML = (html) => {
    if (!html || typeof html !== 'string') return '';
    return DOMPurify.sanitize(html);
};

/**
 * Formats a file size into a human-readable string.
 * @param {number} size - The file size in bytes.
 * @returns {string} The formatted file size string.
 */
export const formattedFileSize = (size) => {
    if (size === null || size === undefined || isNaN(size)) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let unitIndex = 0;
    while (size >= 1024) {
        size /= 1024;
        unitIndex++;
    }

    return size.toFixed(2) + ' ' + units[unitIndex];
};

export const parseBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const lowerValue = value.toLowerCase();
        return lowerValue === 'true' || lowerValue === '1';
    }
    return Boolean(value);
};